<template>
  <h1 class="sr-only">전시 지도</h1>
  <!-- s::EXHIBITION MAP (mimio#30/#33) -->
  <div
    class="exmap"
    :class="{
      'is-mobile': isMobile,
      'is-railoff': railCollapsed && !isMobile,
      'is-sheetdrag': isMobile && liveHeight !== null,
    }"
    :style="{ '--sheet-h': liveSheetPx + 'px', '--card-lift': cardLiftPx + 'px' }"
  >
    <div ref="mapEl" class="exmap__canvas"></div>

    <!-- SDK 를 못 받았을 때. 조용히 빈 화면이 되지 않게 원인과 다음 행동을 그대로 적는다. -->
    <div v-if="mapError" class="exmap__fail">
      <b>지도를 불러오지 못했어요.</b>
      <p>{{ mapError.message }}</p>
      <p v-if="mapError.code === 'LOAD_FAILED'" class="exmap__fail-origin">
        현재 주소: <code>{{ currentOrigin }}</code>
      </p>
      <router-link class="exmap__fail-link" :to="listRoute">목록으로 돌아가기</router-link>
    </div>

    <div class="exmap__top">
      <router-link class="exmap__back" :to="listRoute">← 목록으로</router-link>
      <div class="exmap__topright">
        <button type="button" class="exmap__icon" title="현재 위치" @click="moveToCurrentPosition">
          <svg viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="7" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
          </svg>
        </button>
        <button
          v-if="isLoggedIn"
          type="button"
          class="exmap__icon"
          :class="{ 'is-on': onlyLiked }"
          title="찜한 전시만"
          @click="toggleOnlyLiked"
        >
          <svg viewBox="0 0 24 24">
            <path d="M12 20s-7-4.6-7-9.4A3.9 3.9 0 0 1 12 8a3.9 3.9 0 0 1 7 2.6C19 15.4 12 20 12 20z" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 시트를 크게 펼치면 지도 띠가 얇아져 줌 버튼이 상단 도크 뒤로 들어간다. 그때는 감춘다. -->
    <div v-show="!(isMobile && snapIndex >= 2)" class="exmap__zoom">
      <button type="button" class="exmap__icon" title="확대" @click="zoom(-1)">+</button>
      <div class="exmap__level">{{ mapLevel ? 'LV ' + mapLevel : '—' }}</div>
      <button type="button" class="exmap__icon" title="축소" @click="zoom(1)">−</button>
    </div>

    <div ref="dockEl" class="exmap__dock">
      <!-- 기간 스크러버는 데스크톱 전용. 375 폭에서 잡아 끌 여유가 없어 모바일은 프리셋으로 대신한다. -->
      <div v-if="!isMobile" class="exmap__scrub">
        <span class="exmap__scrub-label">기간</span>
        <input v-model.number="dayOffset" type="range" min="0" max="180" step="1" aria-label="기준 날짜" />
        <span class="exmap__scrub-value">{{ scrubLabel }}</span>
      </div>

      <div class="exmap__row">
        <label class="exmap__search">
          <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg>
          <input
            v-model="searchInput"
            type="search"
            placeholder="전시명 또는 장소명"
            @keyup.enter="applySearch"
          />
        </label>
        <button
          v-for="preset in PRESETS"
          :key="preset.key"
          type="button"
          class="exmap__chip"
          :class="{ 'is-on': activePreset === preset.key }"
          @click="selectPreset(preset.key)"
        >
          {{ preset.label }}
        </button>
      </div>

      <div class="exmap__row exmap__row--regions">
        <button
          type="button"
          class="exmap__chip"
          :class="{ 'is-on': activeRegion === null }"
          @click="selectRegion(null)"
        >
          전국<span class="exmap__n">{{ periodFiltered.length }}</span>
        </button>
        <button
          v-for="region in availableRegions"
          :key="region.name"
          type="button"
          class="exmap__chip"
          :class="{ 'is-on': activeRegion === region.name }"
          @click="selectRegion(region.name)"
        >
          {{ region.name }}<span class="exmap__n">{{ region.count }}</span>
        </button>
      </div>
    </div>

    <!-- 목록 레일 / 모바일 바텀시트 -->
    <aside
      ref="railEl"
      class="exmap__rail"
      :class="['is-snap-' + snapIndex, { 'is-dragging': liveHeight !== null, 'is-min': isMobile && snapIndex === 0 }]"
      @pointerdown="onSheetDown"
      @pointermove="onSheetMove"
      @pointerup="onSheetUp"
      @pointercancel="onSheetUp"
    >
      <!-- 데스크톱은 레일이 폭을 380px 상시 차지한다. 지도를 넓게 보려면 접을 수 있어야 한다. -->
      <button
        v-if="!isMobile"
        type="button"
        class="exmap__railtoggle"
        :title="railCollapsed ? '목록 펼치기' : '목록 접기'"
        @click="toggleRail"
      >
        {{ railCollapsed ? '‹' : '›' }}
      </button>

      <!-- 그랩바는 표식일 뿐 — 드래그는 시트 전체(머리말·목록 최상단)에서 받는다.
           18px 짜리 손잡이만 잡히면 실제로는 못 잡는다. -->
      <div v-if="isMobile" class="exmap__grab" aria-hidden="true">
        <span class="exmap__grab-bar"></span>
      </div>

      <div class="exmap__railhead" :class="{ 'is-compact': isMobile }" @click="onHeadClick">
        <h2 class="exmap__count">
          <em>{{ railItems.length }}</em>
          <!-- 시트가 지도를 덮으므로 "화면에" 는 더 이상 정확하지 않다 — 시트 뒤의 핀도 이 목록에 있다. -->
          <span>{{ sheetFull ? '개의 전시 · 지도 밖까지' : '개의 전시가 이 지도 안에 있어요' }}</span>
        </h2>
        <div class="exmap__sort">
          <span>정렬</span><b>종료 임박순</b><span aria-hidden="true">·</span><b>{{ scrubLabel }}</b>
        </div>
      </div>

      <!-- 스크롤 컨테이너는 시트 본문 전체. 헤더/푸터를 안쪽에 겹쳐두면 실제 스크롤 창이 화면의 한 뼘밖에 안 남는다. -->
      <div ref="railListEl" class="exmap__raillist">
        <p v-if="loading" class="exmap__empty">불러오는 중…</p>
        <p v-else-if="!railItems.length" class="exmap__empty">
          {{
            sheetFull
              ? '조건에 맞는 전시가 없어요. 기간·지역을 바꿔보세요.'
              : '이 지도 범위에는 전시가 없어요. 지도를 움직이거나 기간·지역을 바꿔보세요.'
          }}
        </p>

        <button
          v-for="item in railItems"
          :key="item.uuid"
          type="button"
          class="exmap__rowitem"
          :class="{ 'is-sel': selected && selected.uuid === item.uuid }"
          @click="selectItem(item, true)"
        >
          <span class="exmap__thumb"><img :src="posterOf(item)" alt="" loading="lazy" /></span>
          <span class="exmap__meta">
            <strong>{{ item.title }}</strong>
            <span class="exmap__venue">{{ venueOf(item) }}</span>
            <span class="exmap__period">{{ periodOf(item) }}</span>
            <span v-if="item.dday !== null" class="exmap__dday" :class="{ 'is-soon': item.dday <= 14 }">
              {{ item.dday < 0 ? '종료' : 'D-' + item.dday }}
            </span>
          </span>
        </button>

        <div class="exmap__railfoot">
          <div v-if="offscreenCount && !sheetFull">
            <span>이 지도 밖의 전시</span><b>{{ offscreenCount }}건</b>
          </div>
          <div v-if="unmappedCount">
            <span>좌표 미확보로 지도에 없는 전시</span><b class="is-warn">{{ unmappedCount }}건</b>
          </div>
        </div>
      </div>
    </aside>

    <!-- 요약 카드 -->
    <div
      v-if="selected"
      ref="cardEl"
      class="exmap__card"
      :class="{ 'is-dragging': cardDrag > 0 }"
      :style="[cardStyle, isMobile && cardDrag ? { transform: `translateY(${cardDrag}px)` } : null]"
      @pointerdown="onCardDown"
      @pointermove="onCardMove"
      @pointerup="onCardUp"
      @pointercancel="onCardUp"
    >
      <button type="button" class="exmap__cardclose" aria-label="닫기" @click="clearSelection">×</button>
      <!-- 모바일은 아래로 밀어 닫는 게 기본 동작이라 손잡이를 눈에 보이게 둔다. -->
      <span v-if="isMobile" class="exmap__cardgrab" aria-hidden="true"></span>
      <div class="exmap__cardposter">
        <img :src="posterOf(selected)" alt="" />
        <span class="exmap__cardgrad"></span>
        <span v-if="selected.dday !== null" class="exmap__carddd">
          {{ selected.dday < 0 ? '종료' : 'D-' + selected.dday }}
        </span>
      </div>
      <div class="exmap__cardbody">
        <h3>{{ selected.title }}</h3>
        <!-- 모바일 카드는 지도를 덮지 않는 게 우선이라 사실 3줄을 두 줄로 접는다.
             데스크톱은 지도가 넓어 그럴 이유가 없어 기존 dl 을 그대로 쓴다. -->
        <template v-if="isMobile">
          <p class="exmap__cardline">{{ venueOf(selected) }}</p>
          <p class="exmap__cardline exmap__cardline--dim">{{ periodOf(selected) }}</p>
        </template>
        <dl v-else class="exmap__facts">
          <div><dt>기간</dt><dd>{{ periodOf(selected) }}</dd></div>
          <div v-if="hoursOf(selected)"><dt>관람</dt><dd>{{ hoursOf(selected) }}</dd></div>
          <div><dt>장소</dt><dd>{{ venueOf(selected) }}</dd></div>
        </dl>
        <div class="exmap__acts">
          <router-link
            class="exmap__act exmap__act--primary"
            :to="{ name: 'ExhibitionDetail', params: { uuid: selected.uuid } }"
          >
            상세보기
          </router-link>
          <a
            class="exmap__act"
            :href="routeLinkOf(selected)"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg viewBox="0 0 24 24">
              <path d="M12 21s-7-6.3-7-11a7 7 0 1 1 14 0c0 4.7-7 11-7 11z" /><circle cx="12" cy="10" r="2.4" />
            </svg>
            길찾기
          </a>
          <HeartButton
            v-model:isLike="selected.isLike"
            class="exmap__act exmap__act--heart"
            :uuid="selected.uuid"
            :like-type="selected.likeType"
          />
        </div>
      </div>
    </div>

    <div v-if="toastMessage" class="exmap__toast">{{ toastMessage }}</div>
  </div>
  <!-- e::EXHIBITION MAP -->
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { apiClient } from '@/services/auth-header';
import { useAuthStore } from '@/store/auth.module';
import { useDeviceStore } from '@/store/deviceStore';
import HeartButton from '@/components/ui/HeartButton.vue';
import { loadKakaoMapSdk } from '@/composables/useKakaoMap';
import { REGION_ORDER, resolveRegion } from '@/utils/exhibitionMapRegion';

const route = useRoute();
const authStore = useAuthStore();
const deviceStore = useDeviceStore();

const isMobile = computed(() => deviceStore.isMobile);
const isLoggedIn = computed(() => authStore.status.loggedIn);
const currentOrigin = window.location.origin;

/** 목록으로 돌아갈 때 진입 당시의 필터·검색어를 그대로 되돌려준다. */
const listRoute = computed(() => ({
  name: 'Exhibition',
  query: {
    ...(route.query.q ? { q: route.query.q } : {}),
    ...(route.query.filter ? { filter: route.query.filter } : {}),
  },
}));

/* ───────── 기간 프리셋 ─────────
   지도는 좌표가 있는 전시를 filter=ALL 로 한 번만 받고, 기간 좁히기는 전부 클라이언트에서 한다.
   프리셋을 누를 때마다 서버를 다시 때리면 지도가 매번 껌뻑이고, 318건 규모에서는 그럴 이유가 없다. */
const PRESETS = [
  { key: 'today', label: '오늘' },
  { key: 'weekend', label: '이번 주말' },
  { key: 'month', label: '이번 달' },
  { key: 'soon', label: '종료 임박' },
];

const MS_DAY = 86400000;

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** 'yyyy.MM.dd.' (백엔드 DateUtil.FORMAT_YYYYMMDD_DOT) → Date. 형식이 다르면 null. */
function parseDotDate(value) {
  if (!value) return null;
  const m = String(value).match(/(\d{4})\.(\d{1,2})\.(\d{1,2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

const items = ref([]);
const unmappedCount = ref(0);
const loading = ref(true);
const mapError = ref(null);
const toastMessage = ref('');
const mapLevel = ref(null);

const searchInput = ref(route.query.q || '');
const appliedQuery = ref(route.query.q || '');
const activePreset = ref(PRESETS.some((p) => p.key === route.query.period) ? route.query.period : 'today');
const activeRegion = ref(route.query.region || null);
const dayOffset = ref(Number(route.query.day) > 0 ? Number(route.query.day) : 0);
const onlyLiked = ref(route.query.liked === '1');

const selected = ref(null);
const visibleItems = ref([]);
const cardStyle = ref(null);

const mapEl = ref(null);
const railListEl = ref(null);
const railEl = ref(null);
const dockEl = ref(null);
const cardEl = ref(null);

/* 모바일 카드는 지도 하단을 가로로 다 쓴다. 줌·토스트가 그 뒤로 들어가지 않게 카드 높이만큼 띄운다.
   높이는 제목 줄 수에 따라 달라져서 상수로 둘 수 없다 — 열릴 때 실제로 잰다. */
const cardHeight = ref(0);
const cardLiftPx = computed(() =>
  isMobile.value && selected.value && cardHeight.value ? cardHeight.value + 44 : 0
);

let kakao = null;
let map = null;
let overlays = [];
let hereOverlay = null;
let toastTimer = null;
let resizeRaf = null;

/* ───────── 데이터 ───────── */

const referenceDate = computed(() => new Date(startOfToday().getTime() + dayOffset.value * MS_DAY));

const scrubLabel = computed(() => {
  const d = referenceDate.value;
  if (dayOffset.value === 0) return `오늘 · ${d.getMonth() + 1}.${d.getDate()}`;
  return `${d.getMonth() + 1}.${d.getDate()}`;
});

function decorate(raw) {
  const from = parseDotDate(raw.periodFrom);
  const to = parseDotDate(raw.periodTo);
  const lat = raw.latitude == null ? null : Number(raw.latitude);
  const lng = raw.longitude == null ? null : Number(raw.longitude);
  return {
    ...raw,
    lat,
    lng,
    from,
    to,
    region: resolveRegion(lat, lng),
    dday: to ? Math.round((to.getTime() - startOfToday().getTime()) / MS_DAY) : null,
  };
}

async function fetchItems() {
  loading.value = true;
  try {
    const { data } = await apiClient.get('/v1/contents/articles/exhibitions/map', {
      params: { filter: 'ALL', ...(appliedQuery.value ? { q: appliedQuery.value } : {}) },
    });
    items.value = (data.items || []).map(decorate);
    unmappedCount.value = data.unmappedCount || 0;
  } catch (e) {
    items.value = [];
    unmappedCount.value = 0;
    toast('전시 목록을 불러오지 못했어요.');
  } finally {
    loading.value = false;
  }
}

/** 기준 날짜에 열려 있는지 — 종료일 당일까지 포함(목록 페이지 Ongoing 판정과 같은 경계). */
function isOpenOn(item, date) {
  if (!item.from || !item.to) return false;
  return item.from.getTime() <= date.getTime() && item.to.getTime() >= date.getTime();
}

function overlapsRange(item, start, end) {
  if (!item.from || !item.to) return false;
  return item.from.getTime() <= end.getTime() && item.to.getTime() >= start.getTime();
}

const periodFiltered = computed(() => {
  const base = referenceDate.value;
  let pool = items.value;
  if (onlyLiked.value) pool = pool.filter((it) => it.isLike);

  if (activePreset.value === 'weekend') {
    // 이번 주 토·일. 이미 주말이 지났으면 다음 주말.
    const sat = new Date(base);
    sat.setDate(sat.getDate() + ((6 - sat.getDay() + 7) % 7));
    const sun = new Date(sat.getTime() + MS_DAY);
    return pool.filter((it) => overlapsRange(it, sat, sun));
  }
  if (activePreset.value === 'month') {
    const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    return pool.filter((it) => overlapsRange(it, base, end));
  }
  if (activePreset.value === 'soon') {
    const limit = new Date(base.getTime() + 14 * MS_DAY);
    return pool.filter((it) => isOpenOn(it, base) && it.to && it.to.getTime() <= limit.getTime());
  }
  return pool.filter((it) => isOpenOn(it, base));
});

const availableRegions = computed(() => {
  const counts = new Map();
  periodFiltered.value.forEach((it) => {
    if (it.region) counts.set(it.region, (counts.get(it.region) || 0) + 1);
  });
  return REGION_ORDER.filter((name) => counts.has(name)).map((name) => ({ name, count: counts.get(name) }));
});

/** 지도에 실제로 얹을 집합 — 기간·지역까지 반영. 뷰포트 판정은 render() 에서. */
const plotted = computed(() =>
  periodFiltered.value.filter((it) => (activeRegion.value ? it.region === activeRegion.value : true))
);

const offscreenCount = computed(() => Math.max(0, plotted.value.length - visibleItems.value.length));

/* ───────── 표시 헬퍼 ───────── */

const posterOf = (it) => it.previewImageUrl || it.imageUrl;
const venueOf = (it) => [it.location, it.locationDetail].filter(Boolean).join(' · ');
const stripDot = (v) => (v || '').replace(/\.$/, '');
const periodOf = (it) => `${stripDot(it.periodFrom)} — ${stripDot(it.periodTo)}`;
const hoursOf = (it) => (it.timeFrom && it.timeTo ? `${it.timeFrom} — ${it.timeTo}` : it.periodMemo || '');

/** 길찾기는 카카오맵 링크로 위임한다. JS SDK 로는 경로선을 그릴 수 없다(공식 문서 확인). */
function routeLinkOf(it) {
  return `https://map.kakao.com/link/to/${encodeURIComponent(it.location || it.title)},${it.lat},${it.lng}`;
}

function toast(message) {
  toastMessage.value = message;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toastMessage.value = '';
  }, 3200);
}

/* ───────── 지도 가시 영역 ─────────
   뷰포트가 아니라 "크롬을 뺀 실제로 보이는 지도" 가 기준이다.
   상단 도크와 우측 레일 뒤에 핀이 묻히면 목록 건수와 화면이 어긋난다.

   모바일은 시트가 지도를 **덮는다** — 지도 컨테이너는 시트 높이와 무관하게 항상 전체다.
   그래서 판정 기준도 시트 높이를 보지 않는다. 예전처럼 "시트를 뺀 띠" 로 재면
   목록을 넓힐수록 띠가 얇아져 22건이 0건이 됐다(실측). 넓히는 행위의 목적과 정반대다. */
function visibleRect() {
  const el = mapEl.value;
  const w = el ? el.clientWidth : window.innerWidth;
  const h = el ? el.clientHeight : window.innerHeight;
  if (isMobile.value) {
    return { left: 12, right: w - 12, top: 150, bottom: h - 12 };
  }
  return { left: 40, right: w - (railW.value + 40), top: 96, bottom: h - 40 };
}

const RAIL_WIDTH = 380;
const railCollapsed = ref(false);
/** 레일이 실제로 먹고 있는 폭. 접었거나 모바일이면 0 — 핀 판정·카드 배치가 이 값을 따라야 한다. */
const railW = computed(() => (isMobile.value || railCollapsed.value ? 0 : RAIL_WIDTH));

function toggleRail() {
  railCollapsed.value = !railCollapsed.value;
  nextTick(() => {
    if (!map) return;
    map.relayout();
    render();
    positionCard();
  });
}

/* ───────── 핀 렌더 ───────── */

function toPoint(item) {
  const p = map.getProjection().containerPointFromCoords(new kakao.maps.LatLng(item.lat, item.lng));
  return { x: p.x, y: p.y };
}

/** 화면 좌표 기준으로 묶는다 — 겹쳐 보이는 것끼리 묶여야 하므로 줌마다 결과가 달라지는 게 맞다. */
function clusterize(list) {
  const radius = isMobile.value ? 40 : 46;
  const groups = [];
  list.forEach((item) => {
    const p = toPoint(item);
    const hit = groups.find((g) => Math.hypot(g.x - p.x, g.y - p.y) < radius);
    if (hit) {
      hit.items.push(item);
      const n = hit.items.length;
      hit.x = (hit.x * (n - 1) + p.x) / n;
      hit.y = (hit.y * (n - 1) + p.y) / n;
      hit.lat = (hit.lat * (n - 1) + item.lat) / n;
      hit.lng = (hit.lng * (n - 1) + item.lng) / n;
    } else {
      groups.push({ x: p.x, y: p.y, lat: item.lat, lng: item.lng, items: [item] });
    }
  });
  return groups;
}

function clearOverlays() {
  overlays.forEach((o) => o.setMap(null));
  overlays = [];
}

function buildPinElement(group) {
  const el = document.createElement('div');
  if (group.items.length > 1) {
    el.className = 'exmap-pin exmap-pin--cluster';
    const deck = group.items
      .slice(0, 3)
      .map((it) => `<i><img src="${posterOf(it)}" alt=""></i>`)
      .join('');
    el.innerHTML = `<div class="exmap-pin__deck">${deck}</div><div class="exmap-pin__count">${group.items.length}</div>`;
    el.title = group.items.map((it) => it.title).join('\n');
    el.addEventListener('click', () => {
      pinClickAt = Date.now();
      map.setLevel(Math.max(1, map.getLevel() - 2), { anchor: new kakao.maps.LatLng(group.lat, group.lng) });
    });
    return el;
  }

  const item = group.items[0];
  const isSel = selected.value && selected.value.uuid === item.uuid;
  el.className = 'exmap-pin exmap-pin--poster' + (isSel ? ' is-sel' : '');
  el.innerHTML = `<div class="exmap-pin__disc"><img src="${posterOf(item)}" alt=""></div><div class="exmap-pin__stem"></div>`;
  el.title = item.title;
  el.addEventListener('click', (e) => {
    e.stopPropagation();
    // 오버레이는 clickable:false 라 지도의 click 도 같이 뜬다(그래야 핀 위에서도 드래그가 된다).
    // 방금 핀을 눌렀다는 사실을 남겨서, 지도 클릭 핸들러가 그 자리에서 선택을 지우지 않게 한다.
    pinClickAt = Date.now();
    selectItem(item, false);
  });
  return el;
}

let pinClickAt = 0;
let rendering = false;

function render() {
  if (!map || rendering) return;
  rendering = true;
  try {
    const rect = visibleRect();
    const inView = plotted.value.filter((it) => {
      if (it.lat == null || it.lng == null) return false;
      const p = toPoint(it);
      return p.x >= rect.left && p.x <= rect.right && p.y >= rect.top && p.y <= rect.bottom;
    });

    clearOverlays();
    clusterize(inView).forEach((group) => {
      overlays.push(
        new kakao.maps.CustomOverlay({
          map,
          position: new kakao.maps.LatLng(group.lat, group.lng),
          content: buildPinElement(group),
          xAnchor: 0,
          yAnchor: 0,
          zIndex: group.items.length > 1 ? 6 : 5,
        })
      );
    });

    visibleItems.value = inView;
    mapLevel.value = map.getLevel();
    tagKakaoBar();
  } finally {
    rendering = false;
  }
}

/** 핀이 전부 이 사각형 안에 들어왔는지. setBounds 가 요청한 패딩을 지켰는지 확인하는 데 쓴다. */
function allInsideRect(list, rect) {
  return list.every((it) => {
    const p = toPoint(it);
    return p.x >= rect.left && p.x <= rect.right && p.y >= rect.top && p.y <= rect.bottom;
  });
}

/** 초기 뷰는 상수 중심·배율이 아니라 데이터 bounds. 상수로 두면 제주·포항이 화면 밖으로 나간다. */
function fitToData(list) {
  if (!map) return;
  const withCoords = list.filter((it) => it.lat != null && it.lng != null);
  if (!withCoords.length) return;
  const bounds = new kakao.maps.LatLngBounds();
  withCoords.forEach((it) => bounds.extend(new kakao.maps.LatLng(it.lat, it.lng)));
  const rect = visibleRect();
  const el = mapEl.value;
  const elH = el ? el.clientHeight : 0;
  const topPad = rect.top + 40;
  const rightPad = Math.max(0, (el ? el.clientWidth : 0) - rect.right) + 40;
  const leftPad = rect.left + 40;
  const applyBounds = (bottomPad) => map.setBounds(bounds, topPad, rightPad, bottomPad, leftPad);

  if (!isMobile.value) {
    applyBounds(Math.max(0, elH - rect.bottom) + 40);
    return;
  }

  // 판정 기준(visibleRect)은 시트를 무시하지만, **맞춰 넣는 화면**은 먼저 시트 위에 들어가게 시도한다.
  applyBounds(sheetHeightPx.value + 40);
  // 다만 카카오는 축소 한계(레벨 상한)에 걸리면 요청한 패딩을 못 지킨다 —
  // 전국에 흩어진 데이터 + 폰 화면이 정확히 그 자리다. 그대로 두면 서울이 상단 도크 뒤로 잘려
  // 운영에서 43건 중 7건만 화면에 남았다(실측). 그때는 시트 몫을 포기하고 컨테이너 전체에 맞춘다.
  // 아래쪽 핀은 시트에 덮이지만 목록에는 그대로 있고, 시트를 내리면 바로 보인다.
  if (!allInsideRect(withCoords, rect)) applyBounds(40);
}

/* ───────── 선택 ───────── */

function selectItem(item, fromList) {
  // 모바일에서 카드가 열리면 시트를 머리말만 남기고 접는다.
  // 접지 않으면 카드(요약) + 시트(목록) + 도크가 지도의 98% 를 덮어 지도를 아예 못 만진다(실측).
  if (isMobile.value && snapIndex.value !== 0) {
    snapRestore = snapIndex.value;
    setSnap(0);
  }
  selected.value = item;
  if (!fromList && map) {
    // 목록에서 고른 게 아니면 카드가 핀을 가리지 않게만 두고 카메라는 건드리지 않는다.
    positionCard();
  } else if (map) {
    map.panTo(new kakao.maps.LatLng(item.lat, item.lng));
    nextTick(positionCard);
  }
  render();
  scrollSelectedIntoView();
}

function clearSelection() {
  const hadCard = selected.value !== null;
  selected.value = null;
  cardDrag.value = 0;
  // 카드 때문에 접었던 시트는 카드를 닫을 때 원래 자리로 돌려준다. 되돌아가지 않으면
  // 핀 한 번 눌렀다고 목록이 영영 접힌 채로 남는다.
  if (hadCard && isMobile.value && snapRestore !== null) {
    const back = snapRestore;
    snapRestore = null;
    setSnap(back);
  }
  render();
}

function scrollSelectedIntoView() {
  // 접힌 시트는 목록이 overflow:hidden 이라 여기서 scrollIntoView 를 부르면 엉뚱한 조상이 스크롤된다.
  if (isMobile.value && snapIndex.value === 0) return;
  nextTick(() => {
    const el = railListEl.value && railListEl.value.querySelector('.exmap__rowitem.is-sel');
    if (el) el.scrollIntoView({ block: 'nearest' });
  });
}

function positionCard() {
  if (!selected.value || !map) return;
  if (isMobile.value) {
    cardStyle.value = null;
    return;
  }
  const p = toPoint(selected.value);
  const el = mapEl.value;
  const vw = el ? el.clientWidth : window.innerWidth;
  const vh = el ? el.clientHeight : window.innerHeight;
  const W = 330;
  const H = 420;
  let left = p.x + 34;
  if (left + W > vw - railW.value - 16) left = p.x - W - 34;
  left = Math.max(16, Math.min(left, vw - railW.value - 16 - W));
  const top = Math.max(96, Math.min(p.y - H / 2, vh - H - 24));
  cardStyle.value = { left: `${left}px`, top: `${top}px` };
}

/* ───────── 컨트롤 ───────── */

function zoom(delta) {
  if (!map) return toast('지도를 불러온 뒤에 사용할 수 있어요.');
  map.setLevel(map.getLevel() + delta);
}

function moveToCurrentPosition() {
  if (!map) return toast('지도를 불러온 뒤에 사용할 수 있어요.');
  if (!navigator.geolocation) return toast('이 브라우저는 현재 위치를 지원하지 않아요.');
  toast('현재 위치를 찾는 중…');
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const ll = new kakao.maps.LatLng(pos.coords.latitude, pos.coords.longitude);
      if (hereOverlay) hereOverlay.setMap(null);
      const el = document.createElement('div');
      el.className = 'exmap-here';
      hereOverlay = new kakao.maps.CustomOverlay({ map, position: ll, content: el, xAnchor: 0, yAnchor: 0, zIndex: 3 });
      map.setLevel(6);
      map.setCenter(ll);
      toast('현재 위치로 이동했어요.');
    },
    () => toast('위치 권한이 필요해요. 브라우저에서 허용해 주세요.'),
    { timeout: 8000 }
  );
}

function applySearch() {
  appliedQuery.value = searchInput.value.trim();
}

function selectPreset(key) {
  activePreset.value = key;
  if (key === 'today') dayOffset.value = 0;
}

function selectRegion(name) {
  activeRegion.value = name;
  clearSelection();
  nextTick(() => {
    if (name) fitToData(plotted.value);
    render();
  });
}

function toggleOnlyLiked() {
  onlyLiked.value = !onlyLiked.value;
}

/* ───────── 모바일 바텀시트 ─────────
   시트는 지도를 **덮는다**(오버레이). 지도 컨테이너는 시트가 어디에 있든 항상 전체 높이다 —
   시트를 올릴수록 지도를 잘라내면 핀도 목록도 같이 사라진다.

   스냅 3단 — 최소화(머리말 한 줄) / 기본 / 최대(도크 아래까지).
   최대는 비율이 아니라 상한값 자체다. 예전엔 0.74·0.9 두 단이 상한에 눌려 32px 차이로 붙어 있어서,
   눌러도 거의 안 움직이는 스냅이 하나 남아 있었다.

   드래그는 손잡이(18px)가 아니라 시트 전체에서 받는다. 손잡이만 잡히면 실기기에서는 사실상 못 잡는다.
   목록에서 시작한 드래그는 목록이 맨 위이고 아래로 끄는 경우에만 시트 드래그로 승격한다 —
   그렇지 않으면 목록 스크롤을 시트가 가로챈다. */
const SHEET_HALF = 0.46;
const snapIndex = ref(1);
/** 최소화 높이는 상수로 두지 않고 실제 머리말 높이를 재서 쓴다 — 폰트·줄바꿈에 따라 달라진다. */
const sheetMinPx = ref(56);
const liveHeight = ref(null);

const viewportHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 800);
/** 전역 헤더가 fixed 라 지도 컨테이너는 뷰포트보다 그만큼 짧다. 뷰포트로 재면 시트가 컨테이너 위로 삐져나가
    카카오 로고를 덮는다(노출은 이용약관 의무). 스냅은 컨테이너 높이 기준으로 잡는다. */
const headerHeight = ref(66);
const containerHeight = computed(() => Math.max(240, viewportHeight.value - headerHeight.value));

/** 시트 상한 — 검색·필터 도크 아래로 제한한다. 도크가 시트 머리말 위에 얹히면 접기 탭이 막힌다(실측). */
const sheetMaxPx = ref(null);

const snapHeights = computed(() => {
  const cap = sheetMaxPx.value || Math.round(containerHeight.value * 0.78);
  const raw = [sheetMinPx.value, Math.round(containerHeight.value * SHEET_HALF), cap].map((h) => Math.min(h, cap));
  // 상한에 걸려 같은 높이가 된 단은 하나로 접는다 — 눌러도 안 움직이는 스냅이 남으면 고장으로 보인다.
  return raw.filter((h, i) => i === 0 || h > raw[i - 1] + 8);
});

const sheetHeightPx = computed(
  () => snapHeights.value[Math.min(snapIndex.value, snapHeights.value.length - 1)]
);

/** 화면이 실제로 쓰는 시트 높이. 드래그 중에는 스냅값이 아니라 손가락을 따라간다.
    시트 위에 얹히는 것들(줌·카드·토스트·카카오 로고 막대)이 전부 이 한 값을 본다 —
    소비자마다 다른 값을 보면 드래그 중에 서로 어긋난다(예전에 128~180px 빈 띠가 그렇게 생겼다). */
const liveSheetPx = computed(() =>
  isMobile.value && liveHeight.value !== null ? liveHeight.value : sheetHeightPx.value
);

/** 카드 때문에 접기 전의 스냅. 사용자가 직접 시트를 움직이면 무효가 된다. */
let snapRestore = null;

/* 시트가 지도를 덮으므로 지도 컨테이너 크기는 시트와 함께 변하지 않는다 —
   드래그 중 relayout 을 따라 붙일 이유도, 지도와 시트 사이가 벌어질 여지도 없어졌다.
   대신 카카오 로고·축척이 시트 뒤로 들어가면 안 되므로(노출은 이용약관 의무) 그 막대만 시트 위로 올린다.
   생성 주체가 SDK 라 우리 마크업에 없다 — 로고 링크를 기준으로 찾아 클래스를 붙이고 CSS 로 띄운다. */
const KAKAO_BAR = 'exmap-kakaobar';

function tagKakaoBar() {
  const cv = mapEl.value;
  if (!cv || cv.querySelector('.' + KAKAO_BAR)) return;
  const link = cv.querySelector('a[href*="map.kakao.com"]');
  if (!link) return;
  let node = link;
  while (node && node.parentElement !== cv) node = node.parentElement;
  if (node) node.classList.add(KAKAO_BAR);
}

/* 시트를 끝까지 올리면 "지도를 보는 화면" 이 아니라 "목록을 읽는 화면" 이다.
   끝단에서는 기간·지역 조건만 남기고 뷰포트 판정을 빼서 지도 밖의 전시까지 같이 읽게 한다. */
const sheetFull = computed(() => isMobile.value && snapIndex.value === snapHeights.value.length - 1);
const railItems = computed(() => (sheetFull.value ? plotted.value : visibleItems.value));

function measureSheetMin() {
  const rail = railEl.value;
  if (!rail || !isMobile.value) return;
  const grab = rail.querySelector('.exmap__grab');
  const head = rail.querySelector('.exmap__railhead');
  const h = (grab ? grab.offsetHeight : 0) + (head ? head.offsetHeight : 0);
  if (h > 20) sheetMinPx.value = Math.round(h);

  if (dockEl.value) {
    const dockBottom = dockEl.value.getBoundingClientRect().bottom - headerHeight.value;
    // 36px 여유는 카카오 로고·축척 막대(19px) + 시트와의 간격(6px) + 도크와의 여백 몫이다.
    // 막대는 시트 위로 올라타므로, 시트 상한을 도크 아래로 충분히 내리지 않으면 이번엔 도크 뒤로 들어간다
    // (노출은 이용약관 의무). 30px 로 뒀을 때 도크와 1px 겹쳤다(실측).
    sheetMaxPx.value = Math.max(200, Math.round(containerHeight.value - dockBottom - 36));
  }
}

function setSnap(next) {
  const clamped = Math.max(0, Math.min(snapHeights.value.length - 1, next));
  snapIndex.value = clamped;
  // 지도 크기는 그대로다(오버레이). 끝단에서 목록 기준이 뷰포트 → 전체로 바뀌므로 선택 행만 다시 보이게 한다.
  scrollSelectedIntoView();
}

/** 놓은 자리와 속도로 붙일 스냅을 정한다. 튕기면 방향 쪽 다음 단, 아니면 가장 가까운 단. */
const FLICK = 0.6; // px/ms

function resolveSnap(height, velocity) {
  const list = snapHeights.value;
  if (velocity > FLICK) {
    for (let i = list.length - 1; i >= 0; i--) if (list[i] < height - 4) return i;
    return 0;
  }
  if (velocity < -FLICK) {
    for (let i = 0; i < list.length; i++) if (list[i] > height + 4) return i;
    return list.length - 1;
  }
  let nearest = 0;
  for (let i = 1; i < list.length; i++) {
    if (Math.abs(list[i] - height) < Math.abs(list[nearest] - height)) nearest = i;
  }
  return nearest;
}

let drag = null;
let sheetMoved = false;

function onSheetDown(e) {
  if (!isMobile.value) return;
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  const inList = !!(e.target.closest && e.target.closest('.exmap__raillist'));
  drag = { y0: e.clientY, h0: sheetHeightPx.value, own: !inList, lastY: e.clientY, lastT: e.timeStamp, v: 0 };
  sheetMoved = false;
  if (drag.own) capture(e);
}

function capture(e) {
  try {
    railEl.value.setPointerCapture(e.pointerId);
  } catch (err) {
    /* 캡처 실패는 치명적이지 않다 — 이벤트가 시트 밖으로 나가면 드래그가 끊길 뿐이다. */
  }
}

function onSheetMove(e) {
  if (!drag) return;
  const dy = e.clientY - drag.y0;

  if (!drag.own) {
    const list = railListEl.value;
    const atTop = !list || list.scrollTop <= 0;
    if (dy > 10 && atTop) {
      // 목록 맨 위에서 아래로 — 시트를 내리는 동작으로 승격. 기준점을 여기서 다시 잡아야 튀지 않는다.
      drag.own = true;
      drag.y0 = e.clientY;
      drag.h0 = sheetHeightPx.value;
      capture(e);
    } else if (Math.abs(dy) > 10) {
      drag = null; // 목록 스크롤에 양보
      return;
    } else {
      return;
    }
  }

  const dt = Math.max(1, e.timeStamp - drag.lastT);
  drag.v = (e.clientY - drag.lastY) / dt;
  drag.lastY = e.clientY;
  drag.lastT = e.timeStamp;

  const list = snapHeights.value;
  const raw = drag.h0 - (e.clientY - drag.y0);
  liveHeight.value = Math.max(list[0], Math.min(raw, list[list.length - 1]));
  if (Math.abs(e.clientY - drag.y0) > 4) sheetMoved = true;
}

function onSheetUp() {
  if (!drag) return;
  const height = liveHeight.value;
  const velocity = drag.v;
  drag = null;
  liveHeight.value = null;
  if (height === null) return;
  // 사용자가 직접 시트를 옮겼으면 "카드 닫을 때 되돌릴 자리" 는 더 이상 유효하지 않다.
  snapRestore = null;
  setSnap(resolveSnap(height, velocity));
}

/** 시트가 드래그를 가져간 동안에는 브라우저의 관성 스크롤을 막는다 (passive:false 로 등록). */
function onSheetTouchMove(e) {
  if (drag && drag.own) e.preventDefault();
}

/** 머리말 탭 — 최소화 ↔ 기본. 드래그 끝의 클릭은 삼킨다. */
function onHeadClick() {
  if (!isMobile.value) return;
  if (sheetMoved) {
    sheetMoved = false;
    return;
  }
  snapRestore = null;
  setSnap(snapIndex.value === 0 ? 1 : 0);
}

/* ───────── 요약 카드 제스처 ─────────
   모바일은 카드가 시트 위에 얹히므로 × 버튼만으로는 좁다. 아래로 밀어 닫는다. */
const cardDrag = ref(0);
let cardDragState = null;

function onCardDown(e) {
  if (!isMobile.value) return;
  if (e.target.closest && e.target.closest('a, button')) return;
  cardDragState = { y0: e.clientY };
}

function onCardMove(e) {
  if (!cardDragState) return;
  cardDrag.value = Math.max(0, e.clientY - cardDragState.y0);
}

function onCardUp() {
  if (!cardDragState) return;
  cardDragState = null;
  const moved = cardDrag.value;
  cardDrag.value = 0;
  if (moved > 60) clearSelection();
}

/** 시트·카드 드래그의 마지막 안전망. 둘 다 진행 중이 아니면 즉시 빠져나간다. */
function onPointerRelease() {
  onSheetUp();
  onCardUp();
}

/** ESC 로 카드 닫기 — 지도에서 가장 흔한 "빠져나가기" 키다. */
function onKeydown(e) {
  if (e.key === 'Escape' && selected.value) clearSelection();
}

/* ───────── URL 동기화 ─────────
   필터 상태를 URL 에 남긴다(공유·새로고침 복원). 단 router 로 넘기지 않는다 —
   App.vue 가 `<router-view :key="$route.fullPath">` 라 쿼리가 한 글자만 바뀌어도 이 화면이 통째로 다시 마운트된다.
   그러면 지역 칩 하나 누를 때마다 지도가 새로 뜨고, 목록을 다시 받고, **시트가 기본 스냅으로 접힌다**(실측:
   최대까지 올려둔 시트가 칩 클릭 직후 snap 2 → 1). URL 만 갈아끼우면 공유·복원은 그대로 살고 화면 상태는 유지된다.
   replace 라 히스토리 항목이 늘지 않는 것도 기존과 같다. */

function syncQuery() {
  const query = {};
  if (appliedQuery.value) query.q = appliedQuery.value;
  if (route.query.filter) query.filter = route.query.filter;
  if (activePreset.value !== 'today') query.period = activePreset.value;
  if (activeRegion.value) query.region = activeRegion.value;
  if (dayOffset.value) query.day = String(dayOffset.value);
  if (onlyLiked.value) query.liked = '1';
  const qs = new URLSearchParams(query).toString();
  window.history.replaceState(window.history.state, '', route.path + (qs ? `?${qs}` : ''));
}

/* ───────── 생명주기 ───────── */

function onWindowResize() {
  viewportHeight.value = window.innerHeight;
  const header = document.querySelector('header');
  if (header) {
    headerHeight.value = header.offsetHeight;
    document.documentElement.style.setProperty('--exmap-top', `${headerHeight.value}px`);
  }
  cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(() => {
    measureSheetMin();
    if (!map) return;
    map.relayout();
    render();
    positionCard();
  });
}

onMounted(async () => {
  // 전역 헤더가 fixed 라 그 높이만큼 내려 앉히고, 지도 화면에서는 본문 스크롤을 잠근다.
  const header = document.querySelector('header');
  headerHeight.value = header ? header.offsetHeight : 66;
  document.documentElement.style.setProperty('--exmap-top', `${headerHeight.value}px`);
  document.body.classList.add('exmap-open');
  window.addEventListener('resize', onWindowResize);
  window.addEventListener('keydown', onKeydown);
  // 창 밖에서 손을 떼면 시트/카드의 pointerup 이 영영 안 온다. 그러면 포인터 캡처가 남아
  // 지도 위 모든 입력이 시트로 끌려가고 지도가 통째로 죽는다. 창 단위로 한 번 더 받는다.
  window.addEventListener('pointerup', onPointerRelease);
  window.addEventListener('pointercancel', onPointerRelease);
  // 시트 드래그 중 브라우저 스크롤을 막으려면 passive 가 아니어야 한다 — 템플릿 @touchmove 로는 못 준다.
  await nextTick();
  if (railEl.value) railEl.value.addEventListener('touchmove', onSheetTouchMove, { passive: false });
  measureSheetMin();

  await fetchItems();

  try {
    kakao = await loadKakaoMapSdk();
  } catch (e) {
    mapError.value = { code: e.code, message: e.message };
    return;
  }

  map = new kakao.maps.Map(mapEl.value, {
    center: new kakao.maps.LatLng(36.5, 127.9),
    level: 13,
    mapTypeControl: false,
    zoomControl: false,
    // 방향키 이동 · +/- 줌. 지도를 키보드로도 쓸 수 있어야 한다.
    keyboardShortcuts: true,
  });

  kakao.maps.event.addListener(map, 'idle', () => {
    render();
    positionCard();
  });
  kakao.maps.event.addListener(map, 'zoom_changed', () => {
    mapLevel.value = map.getLevel();
  });
  // 빈 지도를 누르면 카드를 닫는다 — 모바일에서 × 를 겨냥하는 것보다 이 쪽이 훨씬 빠르다.
  kakao.maps.event.addListener(map, 'click', () => {
    if (Date.now() - pinClickAt < 400) return;
    if (selected.value) clearSelection();
  });

  await nextTick();
  map.relayout();
  fitToData(plotted.value);
  measureSheetMin();
  tagKakaoBar();
  setTimeout(render, 200);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize);
  window.removeEventListener('keydown', onKeydown);
  window.removeEventListener('pointerup', onPointerRelease);
  window.removeEventListener('pointercancel', onPointerRelease);
  if (railEl.value) railEl.value.removeEventListener('touchmove', onSheetTouchMove);
  cancelAnimationFrame(resizeRaf);
  clearTimeout(toastTimer);
  clearOverlays();
  if (hereOverlay) hereOverlay.setMap(null);
  document.body.classList.remove('exmap-open');
});

watch(appliedQuery, async () => {
  clearSelection();
  await fetchItems();
  await nextTick();
  fitToData(plotted.value);
  render();
});

watch([activePreset, dayOffset, onlyLiked], () => {
  clearSelection();
  nextTick(render);
});

watch([appliedQuery, activePreset, activeRegion, dayOffset, onlyLiked], syncQuery);

watch([selected, isMobile], async () => {
  if (!selected.value) {
    cardHeight.value = 0;
    return;
  }
  await nextTick();
  cardHeight.value = cardEl.value ? cardEl.value.offsetHeight : 0;
});

watch(isMobile, () => {
  nextTick(() => {
    measureSheetMin();
    if (!map) return;
    map.relayout();
    fitToData(plotted.value);
    render();
  });
});
</script>

<style>
/* reset.css 가 div·h1~h6·p·li·dt·dd·button·input 에 color:#191919 를 직접 박아둔다.
   전역 배경이 밝은 페이지 기준이라, 검정 크롬 위에서는 제목·칩·아이콘이 통째로 안 보인다
   (실제로 목록의 전시 제목이 배경과 같은 색으로 사라졌다).
   상속을 되살려 놓고, 개별 색은 아래 클래스 규칙이 정한다.
   :is() 로 (0,1,1) 을 만들어 reset 은 이기고 클래스 규칙(0,2,0)에는 지게 맞춘다 —
   여기서 요소 선택자를 그냥 쓰면 반대로 클래스 규칙을 덮어써서 같은 문제가 반복된다. */
.exmap :is(div, p, h1, h2, h3, h4, h5, h6, li, dt, dd, input, textarea, button) {
  color: inherit;
}
/* 같은 이유로 폰트도 여기서 되돌린다. 스코프 블록에 `.exmap button {}` 를 두면
   특이도가 (0,2,1) 이라 .exmap__icon 의 배경·테두리까지 덮어써 버튼이 투명해진다. */
.exmap :is(button, input, textarea) {
  font-family: inherit;
  font-size: inherit;
}

/* 지도 화면에서만 전역 크롬을 접는다. 지도는 뷰포트를 다 써야 하고, 그 아래 푸터로 스크롤될 이유가 없다. */
body.exmap-open {
  overflow: hidden;
}
body.exmap-open footer,
body.exmap-open .floating-menu-box,
body.exmap-open .btn-floating.top {
  display: none !important;
}

/* 카카오 로고·축척 막대. SDK 가 지도 컨테이너 왼쪽 아래에 인라인 스타일(bottom:0)로 만든다.
   모바일은 시트가 지도를 덮으므로 그대로 두면 로고가 시트 뒤로 들어간다 — 노출은 이용약관 의무라 시트 위로 올린다.
   SDK 가 만든 노드라 scoped 규칙은 안 먹고(속성 셀렉터 불일치), 인라인 bottom 을 이기려면 !important 가 필요하다. */
.exmap.is-mobile .exmap-kakaobar {
  bottom: calc(var(--sheet-h, 46%) + 6px) !important;
  transition: bottom 0.22s ease;
}
.exmap.is-mobile.is-sheetdrag .exmap-kakaobar {
  transition: none;
}

/* 핀은 CustomOverlay 안에서 만들어지므로 scoped 밖에 둔다. */
.exmap-pin {
  position: relative;
  cursor: pointer;
}
.exmap-pin--poster {
  transform: translate(-50%, -100%);
}
.exmap-pin__disc {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #0b0b0e;
  background: #222;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.9), 0 6px 16px rgba(0, 0, 0, 0.3);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s;
}
.exmap-pin__disc img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
}
.exmap-pin__stem {
  width: 2px;
  height: 11px;
  margin: 0 auto;
  background: #0b0b0e;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.75);
}
.exmap-pin--poster:hover .exmap-pin__disc,
.exmap-pin--poster.is-sel .exmap-pin__disc {
  transform: scale(1.18);
  border-color: #da4c0f;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35), 0 0 22px rgba(218, 76, 15, 0.8), 0 8px 20px rgba(0, 0, 0, 0.35);
}
.exmap-pin--poster.is-sel .exmap-pin__disc {
  border-width: 3px;
}
.exmap-pin--cluster {
  transform: translate(-50%, -50%);
}
.exmap-pin__deck {
  position: relative;
  width: 54px;
  height: 54px;
}
.exmap-pin__deck i {
  position: absolute;
  inset: 0;
  border-radius: 4px;
  overflow: hidden;
  display: block;
  border: 1.5px solid #0b0b0e;
  background: #222;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6), 0 4px 12px rgba(0, 0, 0, 0.3);
}
.exmap-pin__deck i:nth-child(1) {
  transform: rotate(-9deg) translate(-4px, 2px);
}
.exmap-pin__deck i:nth-child(2) {
  transform: rotate(5deg) translate(3px, -1px);
}
.exmap-pin__deck img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
/* 겹친 마커 개수 — 초록 배지 위 숫자는 검정이어야 읽힌다.
   선택자를 한 단계 더 얹은 건 취향이 아니라 캐스케이드 때문이다.
   scoped 쪽 `.exmap :is(div, ...) { color: inherit }`(0,1,1)이 reset.css 의 `div{color:#191919}`
   를 무력화하려고 걸려 있는데, 이게 `.exmap-pin__count`(0,1,0) 의 color 까지 같이 먹는다.
   그러면 숫자가 --paper(#EBF5E8) 를 상속해 초록 위 흰 글씨(대비 1.1:1)로 사라진다. */
.exmap-pin--cluster .exmap-pin__count {
  color: #000;
}
.exmap-pin__count {
  position: absolute;
  right: -8px;
  top: -8px;
  min-width: 22px;
  height: 22px;
  padding: 0 5px;
  border-radius: 11px;
  background: #da4c0f;
  border: 1.5px solid #0b0b0e;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.exmap-here {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #4da3ff;
  border: 2px solid #fff;
  box-shadow: 0 0 0 6px rgba(77, 163, 255, 0.25), 0 2px 8px rgba(0, 0, 0, 0.35);
  transform: translate(-50%, -50%);
}
@media (max-width: 767px) {
  .exmap-pin__disc {
    width: 34px;
    height: 34px;
  }
  .exmap-pin__deck {
    width: 40px;
    height: 40px;
  }
  .exmap-pin__count {
    min-width: 19px;
    height: 19px;
    font-size: 11px;
    right: -6px;
    top: -6px;
  }
}
</style>

<style scoped>
/* 팔레트 — 검정 크롬 + 오렌지 액센트(#da4c0f).
   지도 타일에는 어떤 보정도 걸지 않는다. 탐색성이 우선이고 아이덴티티는 크롬·핀에서 낸다. */
.exmap {
  --ink: #0b0b0e;
  --paper-rgb: 235, 245, 232;
  --accent-rgb: 218, 76, 15;
  --paper: rgb(var(--paper-rgb));
  --accent: rgb(var(--accent-rgb));
  --line: rgba(var(--paper-rgb), 0.18);
  --line2: rgba(var(--paper-rgb), 0.32);
  --dim: rgba(var(--paper-rgb), 0.55);
  --rail: 380px;
  --dock: 560px;

  position: fixed;
  top: var(--exmap-top, 66px);
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  background: var(--ink);
  color: var(--paper);
  font-family: 'SUIT', 'Poppins', sans-serif;
  overflow: hidden;
}
.exmap__canvas {
  position: absolute;
  inset: 0;
  background: #f2f0ec;
}
/* 모바일 지도는 시트에 잘리지 않는다 — 시트가 그 위를 덮는다.
   지도를 잘라내면 시트를 올릴수록 지도 띠가 얇아져 핀도 목록도 같이 사라진다(22건 → 0건, 실측).
   카카오 로고 막대를 시트 위로 올리는 규칙은 SDK 가 만든 노드라 scoped 밖(위쪽 블록)에 있다. */

/* ── 상단 크롬 ── */
.exmap__top {
  position: absolute;
  top: 0;
  left: 0;
  /* 레일 위로 넘어가면 우측 아이콘이 목록에 얹힌다 — 지도 영역 안에서만 놓는다. */
  right: var(--rail);
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  pointer-events: none;
}
.exmap__top > * {
  pointer-events: auto;
}
.exmap__back {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 13px;
  font-size: 12.5px;
  color: var(--paper);
  text-decoration: none;
  border: 1px solid var(--line);
  border-radius: 2px;
  background: rgba(11, 11, 14, 0.82);
  backdrop-filter: blur(6px);
}
.exmap__back:hover {
  border-color: var(--accent);
  color: var(--accent);
}
.exmap__topright {
  margin-left: auto;
  display: flex;
  gap: 8px;
}
.exmap__icon {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--line);
  background: rgba(11, 11, 14, 0.82);
  backdrop-filter: blur(6px);
  border-radius: 2px;
  color: var(--paper);
}
.exmap__icon:hover {
  border-color: var(--accent);
  color: var(--accent);
}
.exmap__icon.is-on {
  background: var(--accent);
  color: #000;
  border-color: var(--accent);
}
.exmap__icon svg {
  width: 16px;
  height: 16px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.5;
}

.exmap__zoom {
  position: absolute;
  right: calc(var(--rail) + 20px);
  bottom: 108px;
  z-index: 60;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.exmap__zoom .exmap__icon {
  width: 32px;
  height: 32px;
  font-size: 15px;
  font-family: 'Poppins', sans-serif;
}
.exmap__level {
  font-family: 'Poppins', sans-serif;
  font-size: 9px;
  letter-spacing: 0.06em;
  color: var(--dim);
  text-align: center;
  background: rgba(11, 11, 14, 0.82);
  border: 1px solid var(--line);
  padding: 3px 0;
  border-radius: 2px;
}
.exmap.is-mobile .exmap__zoom {
  right: 12px;
  /* --card-lift 는 카드가 열렸을 때만 0 이 아니다. 안 띄우면 줌 버튼이 카드 뒤로 들어간다. */
  bottom: calc(var(--sheet-h, 46%) + 14px + var(--card-lift, 0px));
  transition: bottom 0.2s ease;
}

/* ── 하단 도크 ── */
.exmap__dock {
  position: absolute;
  left: calc(50% - var(--rail) / 2);
  bottom: 22px;
  transform: translateX(-50%);
  z-index: 60;
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: min(var(--dock), calc(100% - 40px));
}
@media (max-width: 1499px) {
  .exmap__dock {
    left: 50%;
  }
}
.exmap__row,
.exmap__scrub {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  background: rgba(11, 11, 14, 0.88);
  backdrop-filter: blur(10px);
  border: 1px solid var(--line);
  border-radius: 2px;
  padding: 5px;
}
.exmap__scrub {
  gap: 10px;
  padding: 7px 14px;
}
.exmap__scrub-label {
  font-family: 'Poppins', sans-serif;
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--accent);
  white-space: nowrap;
}
.exmap__scrub input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  height: 2px;
  background: var(--line2);
  outline: none;
}
.exmap__scrub input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 10px rgba(var(--accent-rgb), 0.8);
  cursor: pointer;
}
.exmap__scrub-value {
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  white-space: nowrap;
  min-width: 78px;
  text-align: right;
}
.exmap__search {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0 10px 0 12px;
  border-right: 1px solid var(--line);
  margin-right: 2px;
  flex: 1;
  min-width: 0;
}
.exmap__search svg {
  width: 14px;
  height: 14px;
  stroke: var(--dim);
  fill: none;
  stroke-width: 1.6;
  flex: 0 0 auto;
}
.exmap__search input {
  background: none;
  border: 0;
  outline: none;
  color: var(--paper);
  font-family: inherit;
  font-size: 12px;
  width: 100%;
  min-width: 0;
}
.exmap__search input::placeholder {
  color: rgba(var(--paper-rgb), 0.35);
}
.exmap__chip {
  font-size: 12px;
  letter-spacing: -0.2px;
  padding: 6px 12px;
  border-radius: 2px;
  color: var(--dim);
  white-space: nowrap;
}
.exmap__chip:hover {
  color: var(--paper);
}
.exmap__chip.is-on {
  background: var(--accent);
  color: #000;
  font-weight: 600;
}
/* 지역 줄은 균등 분할해 세그먼트 컨트롤처럼 정렬한다. */
.exmap__row--regions .exmap__chip {
  flex: 1;
  text-align: center;
  padding-left: 4px;
  padding-right: 4px;
}
.exmap__n {
  font-family: 'Poppins', sans-serif;
  font-size: 10px;
  margin-left: 5px;
  opacity: 0.65;
}

.exmap.is-mobile .exmap__dock {
  bottom: auto;
  top: 56px;
  left: 10px;
  right: 10px;
  transform: none;
  width: auto;
  gap: 7px;
}
.exmap.is-mobile .exmap__row {
  overflow-x: auto;
  flex-wrap: nowrap;
  scrollbar-width: none;
}
.exmap.is-mobile .exmap__row::-webkit-scrollbar {
  display: none;
}
.exmap.is-mobile .exmap__row--regions .exmap__chip {
  flex: 0 0 auto;
  padding-left: 10px;
  padding-right: 10px;
}
/* 모바일 도크는 가로 스크롤이라 검색칸을 너무 좁히면 플레이스홀더가 잘린다.
   최소 폭만 주고 남는 자리를 쓰게 둔다. */
.exmap.is-mobile .exmap__search {
  flex: 1 0 132px;
}
.exmap.is-mobile .exmap__search input {
  width: 100%;
}
.exmap.is-mobile .exmap__top {
  right: 0;
  padding: 13px 12px;
}
.exmap.is-mobile .exmap__back {
  height: 30px;
  padding: 0 11px;
  font-size: 11.5px;
}
.exmap.is-mobile .exmap__icon {
  width: 30px;
  height: 30px;
}

/* ── 목록 레일 ── */
.exmap__rail {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: var(--rail);
  z-index: 55;
  background: rgba(11, 11, 14, 0.94);
  backdrop-filter: blur(14px);
  border-left: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.exmap__railhead {
  padding: 20px 20px 14px;
  border-bottom: 1px solid var(--line);
  flex: 0 0 auto;
}
.exmap__count {
  font-size: 19px;
  font-weight: 600;
  letter-spacing: -0.4px;
  display: flex;
  align-items: baseline;
  gap: 7px;
}
.exmap__count em {
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-size: 26px;
  font-weight: 700;
  color: var(--accent);
}
.exmap__sort {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 11px;
  color: var(--dim);
}
.exmap__sort b {
  color: var(--paper);
  font-weight: 500;
}

.exmap__raillist {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
/* 목록은 세로 스크롤만 브라우저에 넘긴다. 가로 제스처까지 넘기면 시트 드래그 승격 판정이 흔들린다. */
.exmap.is-mobile .exmap__raillist {
  touch-action: pan-y;
}
.exmap__raillist::-webkit-scrollbar {
  width: 4px;
}
.exmap__raillist::-webkit-scrollbar-thumb {
  background: var(--line2);
}
.exmap__empty {
  padding: 28px 20px;
  font-size: 12.5px;
  line-height: 1.7;
  color: var(--dim);
}
.exmap__rowitem {
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 13px 20px;
  text-align: left;
  border-bottom: 1px solid rgba(var(--paper-rgb), 0.07);
}
.exmap__rowitem:hover {
  background: rgba(var(--paper-rgb), 0.05);
}
.exmap__rowitem.is-sel {
  background: rgba(var(--accent-rgb), 0.1);
  box-shadow: inset 3px 0 0 var(--accent);
}
.exmap__thumb {
  width: 52px;
  height: 70px;
  flex: 0 0 auto;
  overflow: hidden;
  background: #1b1b20;
  border: 1px solid var(--line);
}
.exmap__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.exmap__meta {
  min-width: 0;
  flex: 1;
  display: block;
}
.exmap__meta strong {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.3px;
}
.exmap__venue {
  display: block;
  font-size: 11px;
  color: var(--dim);
  margin-top: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.exmap__period {
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 10px;
  color: rgba(var(--paper-rgb), 0.42);
  margin-top: 3px;
}
.exmap__dday {
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: var(--accent);
  margin-top: 5px;
}
.exmap__dday.is-soon {
  color: #ff8a5b;
}
/* 좌표 누락·화면 밖 건수는 목록 끝에 붙인다 — 별도 푸터 줄로 두면 모바일 시트에서 스크롤 창을 또 깎는다. */
.exmap__railfoot {
  padding: 14px 20px 24px;
  font-size: 11px;
  color: rgba(var(--paper-rgb), 0.42);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.exmap__railfoot > div {
  display: flex;
  justify-content: space-between;
}
.exmap__railfoot b {
  font-weight: 500;
  color: rgba(var(--paper-rgb), 0.7);
}
.exmap__railfoot b.is-warn {
  color: #ff8a5b;
}

/* ── 모바일 바텀시트 ── */
.exmap.is-mobile .exmap__rail {
  top: auto;
  left: 0;
  right: 0;
  bottom: 0;
  width: auto;
  height: var(--sheet-h, 46%);
  border-left: 0;
  border-top: 1px solid var(--line2);
  border-radius: 14px 14px 0 0;
  box-shadow: 0 -18px 44px rgba(0, 0, 0, 0.7);
  transition: height 0.22s ease, transform 0.22s ease;
  will-change: height, transform;
}
/* touch-action 은 조상까지 교집합으로 계산된다 — 시트 루트에 none 을 주면 안쪽 목록의 pan-y 까지
   같이 죽어서 목록이 아예 안 스크롤된다(실측). 잡아야 하는 자리에만 none 을 준다. */
.exmap.is-mobile .exmap__railhead {
  touch-action: none;
}
/* 드래그 중에는 높이 트랜지션을 끈다 — 손가락보다 늦게 따라오면 잡은 느낌이 사라진다. */
.exmap.is-mobile .exmap__rail.is-dragging {
  transition: none;
}
/* 최소화 상태에서는 목록이 0 으로 줄어드는데, 잔여 스크롤 위치가 남으면 다시 펼 때 중간부터 보인다. */
.exmap.is-mobile .exmap__rail.is-min .exmap__raillist {
  overflow: hidden;
}
.exmap__grab {
  flex: 0 0 auto;
  padding: 9px 0 5px;
  display: flex;
  justify-content: center;
  touch-action: none;
  cursor: grab;
}
.exmap__grab-bar {
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: var(--line2);
}
/* 시트 머리말은 한 줄로 접는다. 세 줄짜리 블록이 스크롤 창을 그만큼 먹고 있었다. */
.exmap__railhead.is-compact {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 0 16px 9px;
  cursor: pointer;
}
.exmap__railhead.is-compact .exmap__count {
  font-size: 13px;
  gap: 5px;
}
.exmap__railhead.is-compact .exmap__count em {
  font-size: 19px;
}
.exmap__railhead.is-compact .exmap__sort {
  margin-top: 0;
  font-size: 10.5px;
}
.exmap.is-mobile .exmap__rowitem {
  padding: 11px 16px;
}
.exmap.is-mobile .exmap__railfoot {
  padding: 14px 16px 28px;
}

/* ── 데스크톱 레일 접기 ── */
.exmap__railtoggle {
  position: absolute;
  /* 접었을 때 레일 왼쪽 모서리가 뷰포트 오른쪽 끝에 딱 붙는다. 폭과 같은 값으로 빼면
     소수점 반올림에서 바깥 테두리가 1px 잘린다 — 1px 더 당겨 안쪽에 남긴다. */
  left: -31px;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: var(--dim);
  background: rgba(11, 11, 14, 0.94);
  border: 1px solid var(--line);
  border-right: 0;
  border-radius: 3px 0 0 3px;
  backdrop-filter: blur(10px);
  z-index: 1;
}
.exmap__railtoggle:hover {
  color: var(--accent);
  border-color: var(--accent);
}
.exmap__rail {
  transition: transform 0.24s ease;
}
.exmap.is-railoff {
  --rail: 0px;
}
.exmap.is-railoff .exmap__rail {
  transform: translateX(100%);
}

/* ── 요약 카드 ── */
.exmap__card {
  position: absolute;
  z-index: 70;
  width: 330px;
  background: rgba(11, 11, 14, 0.97);
  backdrop-filter: blur(16px);
  border: 1px solid var(--line2);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}
/* 모바일 카드는 세로형이면 지도의 98% 를 덮어 지도를 아예 못 만진다(실측: 지도 434px 중 카드 338px).
   포스터를 왼쪽에 세우고 글을 오른쪽에 눕혀 높이를 반 이하로 줄인다. */
.exmap.is-mobile .exmap__card {
  left: 10px;
  right: 10px;
  top: auto !important;
  /* 34px 는 카카오 로고·축척 몫. 시트만 피하고 카드가 그 위에 앉으면 로고를 덮는다(이용약관 의무). */
  bottom: calc(var(--sheet-h, 46%) + 34px);
  width: auto;
  touch-action: none;
  display: flex;
  align-items: stretch;
  padding: 14px 10px 10px;
  gap: 11px;
  transition: transform 0.2s ease, opacity 0.2s ease, bottom 0.22s ease;
}
.exmap.is-mobile.is-sheetdrag .exmap__card {
  transition: none;
}
.exmap.is-mobile .exmap__card.is-dragging {
  transition: none;
}
/* 아래로 밀어 닫는다는 표식. 이게 없으면 × 버튼만 있는 줄 안다. */
.exmap__cardgrab {
  position: absolute;
  left: 50%;
  top: 7px;
  transform: translateX(-50%);
  width: 38px;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.55);
  z-index: 2;
}
.exmap__cardclose {
  position: absolute;
  right: 8px;
  top: 8px;
  width: 26px;
  height: 26px;
  color: var(--dim);
  font-size: 16px;
  line-height: 1;
  z-index: 2;
}
.exmap__cardclose:hover {
  color: var(--paper);
}
.exmap__cardposter {
  position: relative;
  width: 100%;
  height: 196px;
  overflow: hidden;
  background: #1b1b20;
}
.exmap.is-mobile .exmap__cardposter {
  flex: 0 0 74px;
  width: 74px;
  height: auto;
  align-self: stretch;
  border-radius: 2px;
}
/* 부모 높이가 flex stretch 로 정해지므로 이미지는 절대배치로 채운다 — height:100% 만으로는
   기준 높이가 없어 0 이 되는 브라우저가 있다. */
.exmap.is-mobile .exmap__cardposter img {
  position: absolute;
  inset: 0;
}
.exmap.is-mobile .exmap__cardgrad {
  background: linear-gradient(180deg, rgba(11, 11, 14, 0), rgba(11, 11, 14, 0.72));
}
.exmap.is-mobile .exmap__carddd {
  left: 4px;
  bottom: 4px;
  font-size: 10px;
  padding: 2px 5px;
}
.exmap__cardposter img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.exmap__cardgrad {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(11, 11, 14, 0.35), rgba(11, 11, 14, 0) 45%, rgba(11, 11, 14, 0.9));
}
.exmap__carddd {
  position: absolute;
  left: 14px;
  bottom: 12px;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 12px;
  color: #000;
  background: var(--accent);
  padding: 3px 9px;
  border-radius: 2px;
  letter-spacing: 0.04em;
}
.exmap__cardbody {
  padding: 15px 16px 16px;
}
.exmap.is-mobile .exmap__cardbody {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0 26px 0 0;
  display: flex;
  flex-direction: column;
}
.exmap.is-mobile .exmap__cardbody h3 {
  font-size: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
/* 데스크톱의 dt/dd 표를 모바일에서는 두 줄로 접는다. 표 형식은 세로를 그만큼 더 먹는다. */
.exmap__cardline {
  margin-top: 6px;
  font-size: 11.5px;
  line-height: 1.4;
  color: var(--paper);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.exmap__cardline--dim {
  margin-top: 2px;
  font-family: 'Poppins', sans-serif;
  font-size: 10.5px;
  color: var(--dim);
}
.exmap__cardbody h3 {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.36;
  letter-spacing: -0.4px;
}
.exmap__facts {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.exmap__facts > div {
  display: flex;
  gap: 9px;
  font-size: 12px;
  line-height: 1.45;
}
.exmap__facts dt {
  font-family: 'Poppins', sans-serif;
  font-size: 9px;
  letter-spacing: 0.12em;
  color: var(--accent);
  flex: 0 0 46px;
  padding-top: 3px;
  text-transform: uppercase;
}
.exmap__facts dd {
  margin: 0;
  color: var(--paper);
}
.exmap__acts {
  display: flex;
  gap: 6px;
  margin-top: 15px;
  align-items: stretch;
}
.exmap.is-mobile .exmap__acts {
  margin-top: auto;
  padding-top: 9px;
}
.exmap.is-mobile .exmap__act {
  height: 32px;
  font-size: 11.5px;
}
.exmap.is-mobile .exmap__act--heart {
  flex: 0 0 32px;
}
.exmap__act {
  flex: 1;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid var(--line2);
  font-size: 12px;
  color: var(--paper);
  text-decoration: none;
  border-radius: 2px;
}
.exmap__act svg {
  width: 14px;
  height: 14px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.6;
}
.exmap__act--primary {
  background: var(--accent);
  color: #000;
  border-color: var(--accent);
  font-weight: 600;
}
.exmap__act--primary:hover {
  filter: brightness(1.12);
  color: #000;
}
.exmap__act:hover {
  border-color: var(--accent);
  color: var(--accent);
}
.exmap__act--heart {
  flex: 0 0 38px;
  padding: 0;
}
.exmap__act--heart :deep(svg) {
  width: 18px;
  height: 18px;
}

/* ── 토스트 · 실패 배너 ── */
.exmap__toast {
  position: absolute;
  left: calc(50% - var(--rail) / 2);
  transform: translateX(-50%);
  bottom: 132px;
  z-index: 90;
  max-width: 420px;
  background: rgba(11, 11, 14, 0.94);
  border: 1px solid var(--line2);
  font-size: 12px;
  padding: 9px 14px;
  border-radius: 2px;
}
.exmap.is-mobile .exmap__toast {
  left: 50%;
  bottom: calc(var(--sheet-h, 46%) + 34px + var(--card-lift, 0px));
  max-width: 300px;
}
.exmap__fail {
  position: absolute;
  left: 20px;
  top: 70px;
  z-index: 80;
  max-width: 430px;
  background: rgba(20, 6, 6, 0.94);
  border: 1px solid #7a2b2b;
  border-left: 3px solid #ff5b5b;
  padding: 12px 14px;
  font-size: 12px;
  line-height: 1.65;
  color: #ffd9d9;
}
.exmap__fail b {
  color: #ff8a8a;
}
.exmap__fail p {
  margin-top: 4px;
}
.exmap__fail code {
  font-family: 'Poppins', monospace;
  font-size: 11px;
  background: rgba(255, 255, 255, 0.08);
  padding: 1px 5px;
}
.exmap__fail-link {
  display: inline-block;
  margin-top: 9px;
  color: var(--accent);
  font-size: 12px;
}
.exmap.is-mobile .exmap__fail {
  left: 10px;
  right: 10px;
  top: 132px;
  max-width: none;
}
</style>
